import { ClockCircleOutlined } from '@ant-design/icons';
import { Avatar, Image, Upload, UploadProps } from 'antd';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FileConfig } from '../../../../../constants/config';
import { RootState } from '../../../../../store';
import { TIME_FORMAT } from '../../../../../util/constants';
import { Helper } from '../../../../../util/helper';
import { RemoveImageIcon } from '../../../chatSupport/@svg/remove';
import { UploadFileSvg } from '../../../chatSupport/@svg/upload';
import { SendMessageIcon, UnSendMessageIcon } from '../../@svg/send';
import { FEE_STATUS, FEE_STATUS_LABEL, ThreadTimeline } from './inquiryFeeTypes';
import { InquiryOption } from './inquiryOptions';

export interface ComposerFile {
  preview: string;
  source: File;
}

interface CardComposerProps {
  onSend: (text: string, files: ComposerFile[], price?: number) => void;
  placeholder?: string;
  // ẩn ô nhập giá ở thread お問い合わせ chung (không báo giá)
  showPrice?: boolean;
}

const CardComposer = ({ onSend, placeholder, showPrice = true }: CardComposerProps) => {
  const [text, setText] = useState('');
  const [price, setPrice] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [files, setFiles] = useState<ComposerFile[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const canSend = !!text.trim() || files.length > 0 || !!price.trim();

  // Giữ input cao 34px khi 1 dòng, tăng dần theo nội dung khi xuống dòng (giới hạn theo max-height CSS).
  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = '34px';
    if (el.scrollHeight > el.clientHeight) {
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    autoResize(e.target);
  };

  const handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // chỉ cho số + tối đa 1 dấu chấm thập phân
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleChangeUpload: UploadProps['onChange'] = ({ fileList: newList }) => {
    if (!!newList?.length) {
      setFileList(newList);
      const urls = newList.map((file: any) => ({
        preview: URL.createObjectURL(file?.originFileObj),
        source: file?.originFileObj,
      }));
      setFiles(urls);
    }
  };

  const handleRemove = (file: ComposerFile) => {
    setFiles((prev) => prev.filter((item) => item.preview !== file.preview));
    setFileList((prev) => prev.filter((item) => item?.originFileObj !== file.source));
  };

  const handleSend = () => {
    if (!canSend) return;
    const priceValue = price.trim() ? Number(price) : undefined;
    onSend(text.trim(), files, priceValue);
    setText('');
    setPrice('');
    setFiles([]);
    setFileList([]);
    if (inputRef.current) inputRef.current.style.height = '34px';
  };

  return (
    <div className="thread-card-composer-wrap">
      {files.length > 0 && (
        <div className="thread-card-composer-uploads">
          {files.map((file) => (
            <div key={file.preview} className="thread-card-composer-uploads-item">
              <Image preview={false} width={40} height={40} className="rounded" src={file.preview} />
              <span className="thread-card-composer-uploads-close" onClick={() => handleRemove(file)}>
                <RemoveImageIcon />
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="thread-card-composer">
        {showPrice && (
          <input
            className="thread-card-composer-price"
            inputMode="decimal"
            placeholder="金額 $（任意）"
            value={price}
            onChange={handleChangePrice}
          />
        )}
        <textarea
          ref={inputRef}
          className="thread-card-composer-input"
          rows={1}
          placeholder={placeholder || 'メッセージを入力（Shift+Enterで改行）'}
          value={text}
          onChange={handleChangeText}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Upload
          className="thread-card-composer-img"
          multiple={true}
          maxCount={5}
          showUploadList={false}
          onChange={handleChangeUpload}
          fileList={fileList}
          accept={FileConfig.imageAccept}
        >
          <UploadFileSvg />
        </Upload>
        <span className={`thread-card-composer-send ${canSend ? '' : 'disabled'}`} onClick={handleSend}>
          {canSend ? <SendMessageIcon /> : <UnSendMessageIcon />}
        </span>
      </div>
    </div>
  );
};

const MessageBubble = ({ data }: { data: any }) => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const images = data?.itemMessageImages || [];
  const time = dayjs(data?.createdAt).format(TIME_FORMAT.MESSAGE_TIME);
  const isStaff = +data?.sendSide !== 0;
  const isMine =
    isStaff &&
    ((authUser?.id != null && +data?.sendStaffId === +authUser?.id) ||
      !!data?.sendStaff?.role?.includes(authUser?.role));

  const imageBlock = (alignEnd: boolean) =>
    !!images.length && (
      <div className={`d-flex flex-column ${alignEnd ? 'align-items-end' : ''}`}>
        <span className="item-detail_message-file">
          <Image.PreviewGroup>
            {images.map((img: any, i: number) => (
              <Image key={i} width={98} height={98} className="rounded" src={Helper.getSourceFile(img?.source)} />
            ))}
          </Image.PreviewGroup>
        </span>
      </div>
    );

  // Tin của user (bên trái).
  if (!isStaff) {
    return (
      <div className="item-detail_message_content_other">
        <div className="item-detail_message_content_other_avatar">
          <Avatar size={32} src={Helper.getSourceFile(data?.sendUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_other_text w-100">
          {imageBlock(false)}
          {!!data?.message && <p className="item-detail_message_content_other_text_content m-t-2">{data.message}</p>}
          <p className="item-detail_message_content_other_text_name m-t-2">
            {data?.sendUser?.name || data?.sendUser?.emailAddress}
          </p>
          <p className="item-detail_message_content_other_text_time">{time}</p>
        </div>
      </div>
    );
  }

  // Tin của staff cùng role đang đăng nhập (bên phải, dùng avatar authUser).
  if (isMine) {
    return (
      <div className="item-detail_message_content_me convert-me-send">
        <div className="item-detail_message_content_me_avatar me-send">
          <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_me_text w-100">
          {imageBlock(true)}
          {!!data?.message && (
            <div className="d-flex justify-content-end">
              <p className="item-detail_message_content_me_text_content m-t-2">{data.message}</p>
            </div>
          )}
          <p className="item-detail_message_content_me_text_name m-t-2 me-send">
            {data?.sendStaff?.name || data?.sendStaff?.loginId || authUser?.name || authUser?.loginId}
          </p>
          <p className="item-detail_message_content_me_text_time me-send">{time}</p>
        </div>
      </div>
    );
  }

  // Tin của staff khác role.
  return (
    <div className="item-detail_message_content_me">
      <div className="item-detail_message_content_me_avatar">
        <Avatar size={32} src={Helper.getSourceFile(data?.sendStaff?.avatar?.source)} />
      </div>
      <div className="item-detail_message_content_me_text w-100">
        {imageBlock(false)}
        {!!data?.message && <p className="item-detail_message_content_me_text_content-other m-t-2">{data.message}</p>}
        <p className="item-detail_message_content_me_text_name m-t-2">
          {data?.sendStaff?.name || data?.sendStaff?.loginId}
        </p>
        <p className="item-detail_message_content_me_text_time">{time}</p>
      </div>
    </div>
  );
};

const FeeCard = ({ fee, staffById }: { fee: any; staffById?: Record<number, any> }) => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const status = fee?.status;
  const invalid = status === FEE_STATUS.SUPERSEDED || status === FEE_STATUS.CANCELED;
  const time = fee?.createdAt ? dayjs(fee.createdAt).format(TIME_FORMAT.MESSAGE_TIME) : '';
  const proposedById = fee?.proposedByStaffId ?? fee?.proposedByStaff?.id;
  const proposer = fee?.proposedByStaff ?? (proposedById != null ? staffById?.[proposedById] : undefined);
  const isMine =
    (authUser?.id != null && proposedById != null && +proposedById === +authUser?.id) ||
    !!proposer?.role?.includes(authUser?.role);

  const quote = (
    <div className={`inquiry-fee-quote ${invalid ? 'is-invalid' : ''}`}>
      <div className="inquiry-fee-quote-row">
        <span className="inquiry-fee-quote-title">費用</span>
        <span className="inquiry-fee-quote-amount">${Number(fee?.chargeAmount ?? 0).toFixed(2)}</span>
      </div>
      {status === FEE_STATUS.PROPOSED ? (
        <div className="inquiry-fee-quote-status waiting">
          <ClockCircleOutlined />
          <span>同意・支払い待ち</span>
        </div>
      ) : (
        <div className="inquiry-fee-quote-status">{FEE_STATUS_LABEL[status] || status}</div>
      )}
    </div>
  );

  if (isMine) {
    return (
      <div className="item-detail_message_content_me convert-me-send">
        <div className="item-detail_message_content_me_avatar me-send">
          <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_me_text w-100">
          {!!fee?.note && (
            <div className="d-flex justify-content-end">
              <p className="item-detail_message_content_me_text_content m-t-2">{fee.note}</p>
            </div>
          )}
          <div className="d-flex justify-content-end">{quote}</div>
          <p className="item-detail_message_content_me_text_name m-t-2 me-send">
            {proposer?.name || proposer?.loginId || authUser?.name || authUser?.loginId}
          </p>
          <p className="item-detail_message_content_me_text_time me-send">{time}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="item-detail_message_content_other">
      <div className="item-detail_message_content_other_avatar">
        <Avatar size={32} src={Helper.getSourceFile(proposer?.avatar?.source)} />
      </div>
      <div className="item-detail_message_content_other_text w-100">
        {!!fee?.note && <p className="item-detail_message_content_other_text_content m-t-2">{fee.note}</p>}
        {quote}
        <p className="item-detail_message_content_other_text_name m-t-2">{proposer?.name || proposer?.loginId}</p>
        <p className="item-detail_message_content_other_text_time">{time}</p>
      </div>
    </div>
  );
};

interface InquiryThreadCardProps {
  thread: ThreadTimeline;
  option?: InquiryOption;
  onSend: (text: string, files: ComposerFile[], price?: number) => void;
  placeholder?: string;
  staffById?: Record<number, any>;
}

export const InquiryThreadCard = ({ thread, option, onSend, placeholder, staffById }: InquiryThreadCardProps) => {
  return (
    <div className="thread-card">
      {option && (
        <div className="thread-card-head">
          <span className="thread-card-head-icon">{option.icon}</span>
          <span>{option.label}</span>
        </div>
      )}
      <div className="thread-card-body">
        {thread.timeline?.map((item, idx) =>
          item.kind === 'fee' ? (
            <FeeCard key={`fee-${item.data?.id}-${idx}`} fee={item.data} staffById={staffById} />
          ) : (
            <MessageBubble key={`msg-${item.data?.id}-${idx}`} data={item.data} />
          ),
        )}
      </div>
      <CardComposer onSend={onSend} placeholder={placeholder} showPrice={!!option} />
    </div>
  );
};
